import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Modal as SemanticModal } from "semantic-ui-react";
import { isFunction, isNonEmptyArray, isNonEmptyMap, isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import useDictionary from "hooks/useDictionary";
import { MAX_ACTIVE_COLUMN_HEADERS } from "globals/constants";
import { alphaSortCollectionByKey, buildCompositeKey } from "utils";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Modal from "@cayuse/shared-react.components.modal";

import styles from "./styles.css";

const MODAL_ID = "setViewModal";

const SetView = ({
	isOpen,
	onOpen,
	onClose,
	onUpdate,
	options,
	selections: initialSelections,
	requireKeys
}) => {
	const enhancedOptions = useMemo(() => {
		return options.map(option => ({
			...option,
			compositeKey: buildCompositeKey(option)
		}));
	}, [ options ]);

	const initialSelectionMap = useMemo(() => {
		const alwaysOnOptions = options.filter(
			({ alwaysActive, hiddenFromUI }) =>
				alwaysActive === true && hiddenFromUI === true
		);
		const allSelections = [
			...alwaysOnOptions,
			...initialSelections
		].filter(Boolean);

		return new Map(allSelections.map(selection => ([ buildCompositeKey(selection), selection ])));
	}, [ initialSelections, options ]);
	const [ selections, setSelections ] = useState(new Map());

	useEffect(() => {
		if (isOpen) {
			setSelections(initialSelectionMap);
		}
	}, [ isOpen, initialSelectionMap ]);

	const dictionary = useDictionary("common.setView");
	const numSelections = isNonEmptyMap(selections) ? selections.size : 0;
	const hasSelections = numSelections > 0;
	const sortedOptions = useMemo(() => {
		const mutableOptions = [ ...enhancedOptions ].filter(
			({ hiddenFromUI, reportOnly }) => hiddenFromUI !== true && reportOnly !== true
		);
		alphaSortCollectionByKey(mutableOptions, "columnHeader");

		return mutableOptions;
	}, [ enhancedOptions ]);
	const hasOptions = isNonEmptyArray(sortedOptions);
	const maxOptionsSelected = numSelections >= MAX_ACTIVE_COLUMN_HEADERS;

	const ariaControlsAttr = isOpen ? { [`aria-controls`]: MODAL_ID } : {};

	const handleClose = reason => {
		if (isFunction(onClose)) {
			onClose({ reason });
		}

		const triggerButton = document.getElementById("setViewModalButton");

		if (triggerButton) {
			triggerButton.focus();
		}
	};

	const handleOpen = useCallback(() => {
		if (isFunction(onOpen)) {
			onOpen();
		}
	}, [ onOpen ]);

	const handleCancel = () => {
		setSelections(initialSelectionMap);
		handleClose("cancelled");
	};

	const ensureAlwaysActiveSelections = () => {
		/* to make sure that the alwaysActive options are always included
		(an edge case when alwaysActive selections aren't present in the user's selections)
		 */
		const mutableSelections = new Map(selections);
		enhancedOptions
			.filter(option => option.alwaysActive && !mutableSelections.has(option.compositeKey))
			.forEach(option => mutableSelections.set(option.compositeKey, option));

		const selectionValues = Array.from(mutableSelections.values());
		const alwaysActiveSelections = selectionValues.filter(option => option.alwaysActive);
		const otherSelections = selectionValues.filter(option => !option.alwaysActive);

		const remainingSlots = MAX_ACTIVE_COLUMN_HEADERS - alwaysActiveSelections.length;
		const limitedOtherSelections = otherSelections.slice(0, Math.max(0, remainingSlots));

		return [ ...alwaysActiveSelections, ...limitedOtherSelections ];
	};

	const handleUpdate = () => {
		const missingAlwaysActive = enhancedOptions
			.filter(option => option.alwaysActive)
			.some(option => !selections.has(option.compositeKey));

		const selectionValues = missingAlwaysActive
			? ensureAlwaysActiveSelections()
			: Array.from(selections.values());

		if (isFunction(onUpdate)) {
			onUpdate(selectionValues);
		}

		handleClose("updated");
	};

	const toggleSelection = column => {
		const id = column?.id;
		const mutableSelections = new Map(selections);

		if (isNonEmptyString(id)) {
			const key = column.compositeKey;
			mutableSelections.has(key)
				? mutableSelections.delete(key)
				: mutableSelections.set(key, column);

			setSelections(mutableSelections);
		}
	};

	const triggerButton = useMemo(() => (
		<IconButton icon="cog" primary onClick={handleOpen} {...ariaControlsAttr} aria-haspopup="dialog" id="setViewModalButton">
			{dictionary.triggerButtonLabel || "Set View"}
		</IconButton>
	), [ dictionary, handleOpen ]);

	const isColumnRenderable = ({ columnHeader, id, keys }) => (
		isNonEmptyString(columnHeader) && isNonEmptyString(id) && (requireKeys === false || isNonEmptyArray(keys))
	);

	const differentInitialSelection = () => {
		const selectionValues = Array.from(selections.values());

		if (initialSelections.length !== numSelections) {
			return true;
		}

		return selectionValues.some(selection => {
			const key = buildCompositeKey(selection);
			const columnHeader = initialSelectionMap.get(key)
				? initialSelectionMap.get(key).columnHeader
				: "" ;

			return columnHeader !== selection.columnHeader;
		});
	};

	const shouldDisableUpdate = !hasSelections || !differentInitialSelection();

	return (
		<Modal
			   id={MODAL_ID}
			   closeIcon
			   trigger={triggerButton}
			   open={isOpen}
			   closeOnEscape={true}
			   size="one-third"
			   onClose={() => handleClose("closed")}
			   className={styles.modalWrapper}
			   ariaLabel={dictionary.heading || "Set View"}
		>
			<Modal.Header className="whiteOnBlue"><h2>{dictionary.heading || "Set View"}</h2></Modal.Header>

			<Modal.Content className={styles.modalContent}>
				{hasOptions && isOpen
					? (
						<>
							<span>
								{dictionary.instructions || "Choose which columns to view on the table. This table can show a maximum of eleven columns at a time."}
							</span>

							<ul className={styles.columnList} role="group" aria-label={dictionary.heading || "Set View"}>
								{sortedOptions
									.filter(isColumnRenderable)
									.map((column, index) => {
										const checkboxId = `col-opt-${column.compositeKey}`;
										const isAlwaysActive = column.alwaysActive === true;
										const isChecked = selections.has(column.compositeKey);

										return isAlwaysActive ?
											(
												<li key={`col-opt-${index}`} className={
														`ui checkbox checked disabled ${styles.alwaysChecked}`
												}
												>
													<label htmlFor={checkboxId}>{column.columnHeader}</label>
												</li>
											)
											: (
												<li key={`col-opt-${index}`} className="ui checkbox">
													<input
														type="checkbox"
														id={checkboxId}
														disabled={maxOptionsSelected && !isChecked}
														checked={isChecked}
														onChange={() => toggleSelection(column)}
													/>
													<label htmlFor={checkboxId}>{column.columnHeader}</label>
												</li>
											);
									})}
							</ul>
						</>
					)
					: <span>{dictionary.noOptions || "Unable to find columns for this list"}</span>
				}
			</Modal.Content>

			<Modal.Actions className={styles.footer}>
				<ButtonGroup>
					<Button secondary onClick={handleCancel}>
						{dictionary.cancel || "Cancel"}
					</Button>

					<Button primary onClick={handleUpdate} disabled={shouldDisableUpdate} tooltip={shouldDisableUpdate && "Please select items from the list"}>
						{dictionary.update || "Update"}
					</Button>
				</ButtonGroup>
			</Modal.Actions>

		</Modal>
	);
};

SetView.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	onOpen: PropTypes.func,
	onUpdate: PropTypes.func,
	options: PropTypes.arrayOf(PropTypes.shape({
		keys: PropTypes.arrayOf(
			PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.number
			])
		),
		columnHeader: PropTypes.string
	})),
	requireKeys: PropTypes.bool
};

export default SetView;
